import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../../lib/supabase";
import { useAuth } from "../../../contexts/AuthContext";
import { useCompany } from "../../../contexts/CompanyContext";

interface FinalPresentation {
  id: string;
  title: string;
  summary: string;
  achievements: string;
  challenges: string;
  learnings: string;
  status: "draft" | "submitted" | "reviewed";
  admin_score: number | null;
  admin_feedback: string | null;
  submitted_at: string | null;
  reviewed_at: string | null;
}

const MIN_LEN = 30;
const SCORE_COLORS: Record<number, string> = { 5: "#00C896", 4: "#7A9BFF", 3: "#FF9F43", 2: "#E8375F", 1: "#888888" };
const GRADE: Record<number, string> = { 5: "A+", 4: "A", 3: "B", 2: "C", 1: "D" };

export default function FinalPresentationPanel() {
  const { user } = useAuth();
  const { workspaceId } = useCompany();

  const [record, setRecord] = useState<FinalPresentation | null>(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", summary: "", achievements: "", challenges: "", learnings: "" });

  const fetchData = useCallback(async () => {
    if (!user || !workspaceId) return;
    setLoading(true);
    const { data } = await supabase.from("final_presentations")
      .select("*").eq("workspace_id", workspaceId).eq("user_id", user.id).maybeSingle();
    setRecord(data as FinalPresentation | null);
    if (data) setForm({ title: data.title, summary: data.summary, achievements: data.achievements, challenges: data.challenges, learnings: data.learnings });
    setLoading(false);
  }, [user, workspaceId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const canSubmit = Object.values(form).every(v => v.trim().length >= MIN_LEN);

  const handleSaveDraft = async () => {
    if (!user || !workspaceId) return;
    setSaving(true);
    const payload = { ...form, workspace_id: workspaceId, user_id: user.id, status: "draft", updated_at: new Date().toISOString() };
    if (record) {
      await supabase.from("final_presentations").update(payload).eq("id", record.id);
    } else {
      const { data } = await supabase.from("final_presentations").insert(payload).select().maybeSingle();
      if (data) setRecord(data as FinalPresentation);
    }
    setSaving(false);
    setEditing(false);
    await fetchData();
  };

  const handleSubmit = async () => {
    if (!user || !workspaceId || !canSubmit) return;
    setSaving(true);
    const payload = { ...form, workspace_id: workspaceId, user_id: user.id, status: "submitted", submitted_at: new Date().toISOString(), updated_at: new Date().toISOString() };
    if (record) {
      await supabase.from("final_presentations").update(payload).eq("id", record.id);
    } else {
      await supabase.from("final_presentations").insert(payload);
    }
    setSaving(false);
    setEditing(false);
    await fetchData();
  };

  if (loading) return <div className="bg-[#111112] rounded-xl border border-white/[0.05] p-4 h-24 animate-pulse" />;

  const statusCfg = {
    draft:     { label: "작성 중", color: "#666666", icon: "ri-draft-line" },
    submitted: { label: "제출 완료 · 검토 대기", color: "#FF9F43", icon: "ri-time-line" },
    reviewed:  { label: "평가 완료", color: "#00C896", icon: "ri-checkbox-circle-line" },
  };
  const cfg = record ? statusCfg[record.status] : null;

  return (
    <div className="bg-[#111112] rounded-xl border border-white/[0.05] overflow-hidden">
      <button type="button" onClick={() => setCollapsed(v => !v)}
        className="w-full px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-white/[0.02] transition-colors border-b border-white/[0.05]">
        <div className="flex items-center gap-3">
          <div className={`w-7 h-7 flex items-center justify-center rounded-lg ${record?.status === "reviewed" ? "bg-[#00C896]/15" : record?.status === "submitted" ? "bg-[#FF9F43]/15" : "bg-white/[0.04]"}`}>
            <i className={`ri-presentation-line text-base ${record?.status === "reviewed" ? "text-[#00C896]" : record?.status === "submitted" ? "text-[#FF9F43]" : "text-[#444444]"}`}></i>
          </div>
          <div className="text-left">
            <div className="text-[#F2F2F2] text-sm font-semibold">최종 발표 자료</div>
            <div className="text-[#444444] text-[10px]">
              {cfg ? <span style={{ color: cfg.color }}><i className={`${cfg.icon} mr-1 text-xs`}></i>{cfg.label}</span> : "아직 작성하지 않았어요"}
            </div>
          </div>
        </div>
        {record?.status === "reviewed" && record.admin_score && (
          <div className="flex items-center gap-2">
            <span className="text-2xl font-black" style={{ color: SCORE_COLORS[record.admin_score] }}>{GRADE[record.admin_score]}</span>
          </div>
        )}
        <i className={`ri-arrow-${collapsed ? "down" : "up"}-s-line text-[#555555] text-sm ml-2`}></i>
      </button>

      {!collapsed && (
        <div className="px-4 py-4">
          {record?.status === "reviewed" && (
            <div className="mb-4 bg-[#00C896]/[0.07] border border-[#00C896]/25 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#00C896] text-xs font-bold flex items-center gap-1.5"><i className="ri-award-line text-sm"></i>강사 평가 결과</span>
                <span className="text-3xl font-black" style={{ color: SCORE_COLORS[record.admin_score ?? 3] }}>{GRADE[record.admin_score ?? 3]}</span>
              </div>
              {record.admin_feedback && (
                <p className="text-[#888888] text-xs leading-relaxed mt-2 border-t border-white/[0.06] pt-2">{record.admin_feedback}</p>
              )}
            </div>
          )}

          {!editing && record ? (
            <div className="space-y-3">
              {[
                { label: "발표 제목", value: record.title, icon: "ri-file-text-line" },
                { label: "활동 요약", value: record.summary, icon: "ri-newspaper-line" },
                { label: "주요 성과", value: record.achievements, icon: "ri-trophy-line" },
                { label: "어려웠던 점", value: record.challenges, icon: "ri-error-warning-line" },
                { label: "배운 점 & 성장", value: record.learnings, icon: "ri-lightbulb-line" },
              ].map(f => (
                <div key={f.label} className="bg-[#0C0C0E] rounded-lg border border-white/[0.05] px-3 py-2.5">
                  <div className="text-[#444444] text-[9px] uppercase tracking-widest mb-1 flex items-center gap-1.5">
                    <i className={`${f.icon} text-xs`}></i>{f.label}
                  </div>
                  <p className="text-[#AAAAAA] text-xs leading-relaxed whitespace-pre-wrap">{f.value}</p>
                </div>
              ))}
              {record.status !== "reviewed" && (
                <div className="flex gap-2 pt-1">
                  <button type="button" onClick={() => setEditing(true)}
                    className="flex-1 py-2 rounded-lg border border-white/[0.08] text-[#666666] text-xs cursor-pointer hover:text-[#888888] transition-colors whitespace-nowrap">수정하기</button>
                  {record.status === "draft" && (
                    <button type="button" onClick={handleSubmit} disabled={!canSubmit || saving}
                      className="flex-1 py-2 rounded-lg bg-[#7A9BFF] text-white text-xs font-bold cursor-pointer hover:bg-[#8AAEFF] transition-colors whitespace-nowrap disabled:opacity-50">
                      {saving ? "제출 중..." : "강사에게 제출"}
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {!record && <p className="text-[#444444] text-xs mb-3">시뮬레이션 활동을 정리하고 강사에게 제출하세요. 각 항목은 {MIN_LEN}자 이상 작성해야 합니다.</p>}
              {[
                { key: "title",        label: "발표 제목",      placeholder: "예: K-ArtPEN 일경험 프로그램 성과 발표 — 마케팅팀 김OO", min: 10 },
                { key: "summary",      label: "활동 요약",      placeholder: "이번 시뮬레이션에서 어떤 역할을 맡아 어떤 업무를 수행했는지 전반적으로 서술하세요", min: MIN_LEN },
                { key: "achievements", label: "주요 성과",      placeholder: "수치로 표현할 수 있는 성과(자본금 변화, 태스크 완료 수, 이벤트 대응 등)를 구체적으로 기술하세요", min: MIN_LEN },
                { key: "challenges",   label: "어려웠던 점",    placeholder: "업무 수행 중 어려웠던 상황과 어떻게 극복했는지 서술하세요", min: MIN_LEN },
                { key: "learnings",    label: "배운 점 & 성장", placeholder: "이번 경험을 통해 새롭게 알게 된 것, 앞으로 어떻게 적용할지 서술하세요", min: MIN_LEN },
              ].map(f => {
                const val = form[f.key as keyof typeof form];
                const ok = val.trim().length >= f.min;
                return (
                  <div key={f.key}>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-[#666666] text-xs">{f.label}</label>
                      <span className={`text-[9px] tabular-nums ${ok ? "text-[#00C896]" : "text-[#444444]"}`}>{val.trim().length}/{f.min}+</span>
                    </div>
                    <textarea value={val} onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                      placeholder={f.placeholder} rows={f.key === "title" ? 1 : 3} maxLength={500}
                      className="w-full bg-[#0C0C0E] text-[#CCCCCC] text-xs px-3 py-2.5 rounded-lg border focus:outline-none placeholder-[#2A2A2A] resize-none transition-colors"
                      style={{ borderColor: ok ? "rgba(0,200,150,0.25)" : "rgba(255,255,255,0.08)" }} />
                  </div>
                );
              })}
              <div className="flex gap-2 pt-1">
                {record && <button type="button" onClick={() => setEditing(false)}
                  className="px-4 py-2.5 rounded-xl border border-white/[0.08] text-[#666666] text-sm cursor-pointer hover:text-[#888888] transition-colors whitespace-nowrap">취소</button>}
                <button type="button" onClick={handleSaveDraft} disabled={saving}
                  className="flex-1 py-2.5 rounded-xl border border-white/[0.08] text-[#888888] text-sm cursor-pointer hover:text-[#CCCCCC] transition-colors whitespace-nowrap">
                  {saving ? "저장 중..." : "임시 저장"}
                </button>
                <button type="button" onClick={handleSubmit} disabled={!canSubmit || saving}
                  className="flex-1 py-2.5 rounded-xl bg-[#7A9BFF] text-white text-sm font-bold cursor-pointer hover:bg-[#8AAEFF] transition-all whitespace-nowrap disabled:opacity-40">
                  {saving ? "제출 중..." : "강사에게 제출"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
